import type { ReactNode } from 'react'
import './states.css'

/**
 * The four generic states every data surface in 2.2 will need, plus the shell's
 * own screens live in ./screens.
 *
 * A note on colour: the Light Surface palette has no red and no orange, so the
 * conventional semantic hues for error and warning are unavailable by design.
 * These states are therefore distinguished by WEIGHT, BORDER and neutral ink
 * rather than by hue. Do not reach for a red in 2.2 to make an error louder.
 */

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="esl-state" role="status" aria-live="polite">
      <span className="esl-spinner" aria-hidden="true" />
      <p className="esl-state__title">{label}</p>
    </div>
  )
}

export function ErrorState({
  title = 'Something went wrong',
  detail,
  onRetry,
}: {
  title?: string
  detail?: string | undefined
  onRetry?: (() => void) | undefined
}) {
  return (
    <div className="esl-state esl-state--error" role="alert">
      <p className="esl-state__title">{title}</p>
      {detail ? <p className="esl-state__detail">{detail}</p> : null}
      {onRetry ? (
        <div className="esl-state__action">
          <button type="button" className="esl-btn esl-btn--secondary" onClick={onRetry}>
            Try again
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function EmptyState({
  title,
  detail,
  children,
}: {
  title: string
  detail?: string | undefined
  children?: ReactNode
}) {
  return (
    <div className="esl-state esl-state--empty">
      <p className="esl-state__title">{title}</p>
      {detail ? <p className="esl-state__detail">{detail}</p> : null}
      {children ? <div className="esl-state__action">{children}</div> : null}
    </div>
  )
}

/**
 * Full-viewport loading, for the window between "we have a session" and
 * "session-gate has returned a verdict". Deliberately NOT the app frame with a
 * spinner inside it: the frame must not appear before the gate has answered.
 */
export function FullPageLoading({ label = 'Checking your access…' }: { label?: string }) {
  return (
    <div className="esl-fullpage" role="status" aria-live="polite">
      <span className="esl-spinner" aria-hidden="true" />
      <p className="esl-state__detail">{label}</p>
    </div>
  )
}
